import { createSlice } from '@reduxjs/toolkit';
import { completeStudentProfile, 
	completeTutorProfile, 
	createStudent, 
	createTutorProfile, 
	deleteTutorCertificate,
	deleteTutorDocument,
	getDashboardStat, 
	getUserProfiles, 
	switchToStudentProfile, 
	switchToTutorProfile, 
	tutorLastStep, 
	updateTutorDocuments} from './profileActions'

export const ProfileRole = {
	GUEST: 'guest',
	ADMIN: 'admin',
	TUTOR: 'tutor',
	STUDENT: 'student',
};

export const ProfileStatusEnum = {
	registring: -2,
	waitForApproval: -1,
	pending: 0,
	active: 1,
	inactive: 2,
	rejected: 3,
  };
	

export const TutorDocumentsType = {
	education: 'education',
	experience: 'experience',
	certificate: 'certificate',
}

export const isTutorProfile = (profile) =>
	profile && profile.role === ProfileRole.TUTOR;

const findTutorProfileIndex = (profiles) =>
	profiles.findIndex((v) => isTutorProfile(v));

const upsertProfileByRole = (profiles, profile) => {
	if (!profile?.role) return;
	const profileIndex = profiles.findIndex((p) => p.role === profile.role);
	if (profileIndex !== -1) {
		profiles[profileIndex] = profile;
	} else {
		profiles.push(profile);
	}
};

const dedupeProfilesByRole = (profiles) => {
	const byRole = new Map();
	for (const profile of profiles) {
		if (profile?.role) {
			byRole.set(profile.role, profile);
		}
	}
	return Array.from(byRole.values());
};

const applyTutorDocumentUpdate = (profile, { type, noDocument, newDocs }) => {
	if (!profile) return;

	if (type === TutorDocumentsType.education) {
		profile.noEducation = noDocument;
		if (noDocument) {
			profile.education = [];
		} else if (newDocs?.length) {
			if (!profile.education) profile.education = [];
			profile.education.push(...newDocs);
		}
	} else if (type === TutorDocumentsType.experience) {
		profile.noExperience = noDocument;
		if (noDocument) {
			profile.experience = [];
		} else if (newDocs?.length) {
			if (!profile.experience) profile.experience = [];
			profile.experience.push(...newDocs);
		}
	} else if (type === TutorDocumentsType.certificate) {
		profile.noCertificate = noDocument;
		if (noDocument) {
			profile.certificate = [];
		} else if (newDocs?.length) {
			if (!profile.certificate) profile.certificate = [];
			profile.certificate.push(...newDocs);
		}
	}
};

const syncTutorDocumentUpdate = (state, action) => {
	const { type, noDocument } = action.meta.arg;
	const newDocs = action.payload;
	applyTutorDocumentUpdate(state.currentProfile, { type, noDocument, newDocs });

	const profileIndex = findTutorProfileIndex(state.profiles);
	if (profileIndex !== -1) {
		applyTutorDocumentUpdate(state.profiles[profileIndex], { type, noDocument, newDocs });
	}
};

export const selectTutorProfile = (state) =>
	state.profile.profiles.find(isTutorProfile) ?? null;

const initialState = {
	loading: false,
	currentProfile: null,
	profiles: [],
	error: null,
	success: false,
	stats: {
		students: 0,
		lessons: 0,
		attendance: 0,
		profit: 0,
		pending: 0,
		pendinglessons: 0,
		spent: 0
	}
};

const profileSlice = createSlice({
	name: 'profile',
	initialState,
	reducers: {
		setProfile: (state, action) => {
			if(action.payload == null){
				state.currentProfile = {
					role: ProfileRole.GUEST
				}
			}else{
				state.currentProfile = action.payload;
			}
		},
		setProfiles: (state, action) => {
			state.profiles = dedupeProfilesByRole(action.payload);
		},
		pushProfile: (state, action) => {
			upsertProfileByRole(state.profiles, action.payload);
		},
		resetProfile: (state) => {
			return initialState;
		},
	},
	extraReducers: {
		[getDashboardStat.fulfilled]: (state, action) => {
			state.stats.students = action.payload.students ? action.payload.students : 0;
			state.stats.lessons = action.payload.lessons ? action.payload.lessons : 0;
			state.stats.attendance = action.payload.attendance ? action.payload.attendance : 0;
			state.stats.profit = action.payload.profit ? action.payload.profit : 0;
			state.stats.pending = action.payload.pending ? action.payload.pending : 0;
			state.stats.spent = action.payload.spent ? action.payload.spent : 0;
			state.stats.pendinglessons = action.payload.pendinglessons ? action.payload.pendinglessons : 0;
		},
		[switchToStudentProfile.fulfilled]: (state, action) => {
			console.log(state.profiles)
			// state.currentProfile = action.payload;
		},
		[switchToTutorProfile.fulfilled]: (state, action) => {
			console.log(state.profiles)
			// state.currentProfile = action.payload;
		},
		[completeStudentProfile.fulfilled]: (state, action) => {
			state.currentProfile = { ...action.payload.profile};
			upsertProfileByRole(state.profiles, action.payload.profile);
		},
		
		[createTutorProfile.fulfilled]: (state, action) => {
			upsertProfileByRole(state.profiles, action.payload);
			if (isTutorProfile(state.currentProfile) || !state.currentProfile?._id) {
				state.currentProfile = { ...action.payload };
			}
		},
		[completeTutorProfile.fulfilled]: (state, action) => {
			upsertProfileByRole(state.profiles, action.payload);
			if (state.currentProfile?.role !== ProfileRole.STUDENT) {
				state.currentProfile = { ...action.payload };
			}
		},
		[deleteTutorDocument.fulfilled]: (state, action) => {
			const { type, id } = action.payload;
			const matchesDocId = (doc) =>
				doc &&
				(doc._id === id ||
					doc.id === id ||
					String(doc._id) === String(id) ||
					String(doc.id) === String(id));

			const spliceById = (list) => {
				if (!list?.length) return;
				const idx = list.findIndex(matchesDocId);
				if (idx !== -1) list.splice(idx, 1);
			};

			const isEducation = type === TutorDocumentsType.education;
			const isExperience =
				type === TutorDocumentsType.experience || type === 'experiences';
			const isCertificate =
				type === TutorDocumentsType.certificate || type === 'certificates';

			const profileIndex = findTutorProfileIndex(state.profiles);

			if (isEducation) {
				spliceById(state.currentProfile?.education);
				if (profileIndex !== -1) {
					spliceById(state.profiles[profileIndex].education);
				}
			} else if (isExperience) {
				spliceById(state.currentProfile?.experience);
				if (profileIndex !== -1) {
					spliceById(state.profiles[profileIndex].experience);
				}
			} else if (isCertificate) {
				spliceById(state.currentProfile?.certificate);
				if (profileIndex !== -1) {
					spliceById(state.profiles[profileIndex].certificate);
				}
			}
		},
		[getUserProfiles.fulfilled]: (state, action) => {
			state.profiles = dedupeProfilesByRole(action.payload);
		},
		[tutorLastStep.fulfilled]: (state, action) => {
			// Conflict responses are returned as fulfilled payloads; do not overwrite profile state
			if (action.payload?.existAppointments) return;
			state.currentProfile = action.payload;
			upsertProfileByRole(state.profiles, action.payload);
		},
		[createStudent.fulfilled]: (state, action) => {
			const profile = action.payload?.profile ?? action.payload;
			if (!profile?.role) return;
			state.currentProfile = profile;
			upsertProfileByRole(state.profiles, profile);
		},
		[updateTutorDocuments.fulfilled]: (state, action) => {
			syncTutorDocumentUpdate(state, action);
		},
	},
});

export default profileSlice.reducer;

export const { setProfile, resetProfile, setProfiles, pushProfile } = profileSlice.actions;
